import { useState, useEffect, useCallback } from 'react';
import { useOrgTimezone, formatLocalDateInTz, formatDateInTz } from '../../utils/timezone';
import { useNavigate, useParams } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { Card, CardContent } from '../../components/ui/Card';
import { OrchardMap, HarvestWindowsPanel, CropLoadPanel, SoilSamplesPanel, MaintenancePanel, SatellitePanel, FertiliserPlanPanel } from '../../components/orchard';
import PbrMark from '../../components/cultivar/PbrMark';
import { Sprout, Edit, Trash2, Loader2, Calendar, Layers, TreePine, Eye } from 'lucide-react';
import orchardService from '../../services/orchard';
import type { CultivarArea, Block, Farm, PlantingPlan } from '../../types/orchard';

function CultivarAreaDetail() {
  const timezone = useOrgTimezone();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [area, setArea] = useState<CultivarArea | null>(null);
  const [block, setBlock] = useState<Block | null>(null);
  const [farm, setFarm] = useState<Farm | null>(null);
  const [plan, setPlan] = useState<PlantingPlan | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const areaId = Number(id);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const areaResponse = await orchardService.getCultivarArea(areaId);
      if (!areaResponse.success || !areaResponse.data) {
        setError('Failed to load cultivar area');
        return;
      }
      const areaData = areaResponse.data;
      setArea(areaData);

      // Load block, farm and planting plan in parallel
      const [blockResponse, farmResponse, planResponse] = await Promise.all([
        orchardService.getBlock(areaData.block_id),
        orchardService.getFarm(areaData.farm_id),
        orchardService.getPlantingPlan(areaId),
      ]);

      if (blockResponse.success && blockResponse.data) {
        setBlock(blockResponse.data);
      }
      if (farmResponse.success && farmResponse.data) {
        setFarm(farmResponse.data);
      }
      if (planResponse.success && planResponse.data) {
        setPlan(planResponse.data);
      }
    } catch {
      setError('Failed to load area data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [areaId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const response = await orchardService.deleteCultivarArea(areaId);
      if (response.success) {
        navigate(area?.block_id ? `/orchard/blocks/${area.block_id}` : '/orchard');
      } else {
        setError('Failed to delete area');
        setShowDeleteConfirm(false);
      }
    } catch {
      setError('Failed to delete area. Please try again.');
      setShowDeleteConfirm(false);
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader
          title="Cultivar Area"
          subtitle="Loading..."
          icon={<Sprout className="h-6 w-6 text-white" />}
          showBackButton
        />
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-10 w-10 text-primary-600 animate-spin" />
        </div>
      </div>
    );
  }

  if (error || !area) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader
          title="Cultivar Area"
          subtitle="Error"
          icon={<Sprout className="h-6 w-6 text-white" />}
          showBackButton
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-error-50 border border-error-200 rounded-xl p-6 text-center">
            <p className="text-error-700 mb-4">{error || 'Area not found'}</p>
            <Button variant="outline" onClick={loadData}>Try Again</Button>
          </div>
        </div>
      </div>
    );
  }

  const displayName = area.cultivar_name
    ? area.cultivar_trade_name
      ? `${area.cultivar_name} (${area.cultivar_trade_name})`
      : area.cultivar_name
    : area.cultivar_id
    ? `Cultivar #${area.cultivar_id}`
    : 'Mixed planting (see plan)';

  const cultivarLookup = (id: number): string => {
    const c = plan?.cultivars.find((c) => c.id === id);
    if (!c) return `#${id}`;
    return c.trade_name ? `${c.name} (${c.trade_name})` : c.name;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title={area.name}
        subtitle={displayName}
        icon={<Sprout className="h-6 w-6 text-white" />}
        showBackButton
        actionButtons={
          <div className="flex gap-2">
            {area.cultivar_id && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/cultivar/${area.cultivar_id}?tab=observations`)}
                title="Record an observation against this cultivar"
              >
                <Eye className="h-4 w-4" />
                Evaluate
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/orchard/cultivar-areas/${area.id}/edit`)}
            >
              <Edit className="h-4 w-4" />
              Edit
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={() => setShowDeleteConfirm(true)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        }
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {error && (
          <div className="bg-error-50 border border-error-200 rounded-lg p-4">
            <p className="text-error-700">{error}</p>
          </div>
        )}

        {/* Delete Confirmation */}
        {showDeleteConfirm && (
          <div className="bg-error-50 border border-error-300 rounded-xl p-6">
            <h3 className="text-base font-semibold text-error-900 mb-2">Delete Cultivar Area?</h3>
            <p className="text-sm text-error-700 mb-4">
              This will permanently delete <strong>{area.name}</strong>.
              This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <Button variant="danger" isLoading={deleting} onClick={handleDelete}>
                Yes, Delete Area
              </Button>
              <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-neutral-500 flex-wrap">
          {farm && (
            <>
              <button
                className="text-primary-600 hover:underline"
                onClick={() => navigate(`/orchard/farms/${farm.id}`)}
              >
                {farm.name}
              </button>
              <span>›</span>
            </>
          )}
          {block && (
            <>
              <button
                className="text-primary-600 hover:underline"
                onClick={() => navigate(`/orchard/blocks/${block.id}`)}
              >
                {block.name}
              </button>
              <span>›</span>
            </>
          )}
          <span className="text-neutral-700 font-medium">{area.name}</span>
        </div>

        {/* Cultivar highlight */}
        <div className="bg-purple-50 border border-purple-200 rounded-xl p-5">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
              <Sprout className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <p className="text-xs text-purple-600 font-medium uppercase tracking-wide">Cultivar / Variety</p>
              <p className="text-lg font-bold text-purple-900">{displayName}</p>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border border-neutral-200 p-4 text-center shadow-sm">
            <p className="text-2xl font-bold text-neutral-900">
              {area.area_hectares != null && !Number.isNaN(Number(area.area_hectares))
                ? Number(area.area_hectares).toFixed(2)
                : '—'}
            </p>
            <p className="text-xs text-neutral-500 mt-1">Hectares</p>
          </div>
          <div className="bg-white rounded-xl border border-neutral-200 p-4 text-center shadow-sm">
            <p className="text-2xl font-bold text-success-600">
              {area.tree_count != null ? area.tree_count.toLocaleString() : '—'}
            </p>
            <p className="text-xs text-neutral-500 mt-1">Trees</p>
          </div>
          <div className="bg-white rounded-xl border border-neutral-200 p-4 text-center shadow-sm">
            <p className="text-2xl font-bold text-primary-600">
              {area.row_spacing_meters != null ? `${area.row_spacing_meters}m` : '—'}
            </p>
            <p className="text-xs text-neutral-500 mt-1">Row Spacing</p>
          </div>
          <div className="bg-white rounded-xl border border-neutral-200 p-4 text-center shadow-sm">
            <p className="text-2xl font-bold text-primary-600">
              {area.tree_spacing_meters != null ? `${area.tree_spacing_meters}m` : '—'}
            </p>
            <p className="text-xs text-neutral-500 mt-1">Tree Spacing</p>
          </div>
        </div>

        {/* Map */}
        <div className="rounded-xl overflow-hidden border border-neutral-200 shadow-sm" style={{ height: '380px' }}>
          <OrchardMap
            farmBoundary={farm?.boundary_geometry}
            blocks={block ? [block] : []}
            cultivarAreas={[area]}
            firstRowLine={
              plan?.plan.first_row_start_point && plan?.plan.first_row_end_point
                ? {
                    start: plan.plan.first_row_start_point,
                    end: plan.plan.first_row_end_point,
                  }
                : undefined
            }
            generatedRows={plan?.rows}
            selectedEntityId={area.id}
            selectedEntityType="cultivar-area"
            center={
              area.center_point
                ? [area.center_point.lat, area.center_point.lng]
                : block?.center_point
                ? [block.center_point.lat, block.center_point.lng]
                : undefined
            }
            zoom={17}
          />
        </div>

        {/* Harvest windows — what the spray module's withholding check reads */}
        <HarvestWindowsPanel areaId={areaId} canEdit />

        {/* Sentinel-2 over the planting, from the farm's images */}
        {area.boundary_geometry && (
          <SatellitePanel
            farmId={area.farm_id}
            farmName={farm?.name}
            target={{ type: 'cultivar_area', id: area.id, name: area.name, boundary: area.boundary_geometry }}
          />
        )}

        {/* Crop load counts — measured and counted trees, scaled to the area's crop */}
        <CropLoadPanel areaId={areaId} />

        {/* Soil analysis, and what the crop takes off */}
        <SoilSamplesPanel blockId={area.block_id} areaId={areaId} />
        <MaintenancePanel areaId={areaId} />
        <FertiliserPlanPanel areaId={areaId} />

        {/* Planting plan summary (when configured) */}
        {plan && plan.rows.length > 0 && (
          <Card>
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-neutral-700">Planting plan</h3>
                <p className="text-xs text-neutral-500">
                  {plan.plan.effective_row_count} rows · bearing{' '}
                  {plan.plan.row_bearing_degrees != null
                    ? `${plan.plan.row_bearing_degrees.toFixed(1)}°`
                    : '—'}{' '}
                  · {plan.plan.row_spacing_meters ?? '—'}m between rows ·{' '}
                  {plan.plan.tree_spacing_meters ?? '—'}m between trees
                </p>
              </div>
              {plan.summary.length > 0 ? (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-neutral-500 border-b">
                      <th className="py-1 pr-2">Cultivar</th>
                      <th className="py-1 pr-2 text-right">Primary</th>
                      <th className="py-1 pr-2 text-right">Pollinator</th>
                      <th className="py-1 text-right">Total trees</th>
                    </tr>
                  </thead>
                  <tbody>
                    {plan.summary.map((s) => (
                      <tr key={s.cultivar_id} className="border-b last:border-0">
                        <td className="py-1.5 pr-2">{cultivarLookup(s.cultivar_id)}</td>
                        <td className="py-1.5 pr-2 text-right">
                          {s.primary_trees.toLocaleString()}
                        </td>
                        <td className="py-1.5 pr-2 text-right">
                          {s.pollinator_trees.toLocaleString()}
                        </td>
                        <td className="py-1.5 text-right font-semibold">
                          {s.total_trees.toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="text-sm text-neutral-500">
                  Geometry set but no pattern or cultivars resolved yet. Open the Edit page to
                  finish configuring.
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {plan && plan.rows.length === 0 && (
          <Card>
            <CardContent className="p-5 flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0">
                <h3 className="text-sm font-semibold text-neutral-700">Not laid out in rows</h3>
                <p className="text-xs text-neutral-500">
                  The tree count is an estimate from spacing. Lay out the rows to count trees row
                  by row and record pollinators and mixed rows.
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/orchard/cultivar-areas/${area.id}/edit#rows`)}
              >
                Lay out rows
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Details Card */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card>
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-4">
                <TreePine className="h-4 w-4 text-neutral-500" />
                <h3 className="text-sm font-semibold text-neutral-700">Planting Details</h3>
              </div>
              <dl className="space-y-2.5">
                {area.planting_date && (
                  <div className="flex justify-between text-sm">
                    <dt className="text-neutral-500 flex items-center gap-1.5">
                      <Calendar className="h-3.5 w-3.5" />
                      Planting Date
                    </dt>
                    <dd className="font-medium text-neutral-900">
                      {formatLocalDateInTz(area.planting_date, timezone)}
                    </dd>
                  </div>
                )}
                {(area.rootstock_cultivar_name || area.rootstock) && (
                  <div className="flex justify-between text-sm">
                    <dt className="text-neutral-500">Rootstock</dt>
                    <dd className="font-medium text-neutral-900 flex items-center">
                      {area.rootstock_cultivar_name || area.rootstock}
                      <PbrMark status={area.rootstock_protection_status} />
                    </dd>
                  </div>
                )}
                {area.interstem_cultivar_name && (
                  <div className="flex justify-between text-sm">
                    <dt className="text-neutral-500">Interstem</dt>
                    <dd className="font-medium text-neutral-900 flex items-center">
                      {area.interstem_cultivar_name}
                      <PbrMark status={area.interstem_protection_status} />
                    </dd>
                  </div>
                )}
                {area.graft_union_height_mm != null && (
                  <div className="flex justify-between text-sm">
                    <dt className="text-neutral-500">Graft Union Height</dt>
                    <dd className="font-medium text-neutral-900">{area.graft_union_height_mm} mm</dd>
                  </div>
                )}
                {area.training_system && (
                  <div className="flex justify-between text-sm">
                    <dt className="text-neutral-500">Training System</dt>
                    <dd className="font-medium text-neutral-900">{area.training_system}</dd>
                  </div>
                )}
                {area.tree_supplier && (
                  <div className="flex justify-between text-sm">
                    <dt className="text-neutral-500">Tree Supplier</dt>
                    <dd className="font-medium text-neutral-900">{area.tree_supplier}</dd>
                  </div>
                )}
                {!area.planting_date && !area.rootstock && !area.rootstock_cultivar_name
                  && !area.interstem_cultivar_name && area.graft_union_height_mm == null
                  && !area.training_system && !area.tree_supplier && (
                  <p className="text-sm text-neutral-400">No planting details recorded</p>
                )}
              </dl>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-4">
                <Layers className="h-4 w-4 text-neutral-500" />
                <h3 className="text-sm font-semibold text-neutral-700">Management</h3>
              </div>
              <dl className="space-y-2.5">
                {area.irrigation_zone && (
                  <div className="flex justify-between text-sm">
                    <dt className="text-neutral-500">Irrigation Zone</dt>
                    <dd className="font-medium text-neutral-900">{area.irrigation_zone}</dd>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <dt className="text-neutral-500">Status</dt>
                  <dd>
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        area.active
                          ? 'bg-success-100 text-success-700'
                          : 'bg-neutral-100 text-neutral-500'
                      }`}
                    >
                      {area.active ? 'Active' : 'Inactive'}
                    </span>
                  </dd>
                </div>
                <div className="flex justify-between text-sm">
                  <dt className="text-neutral-500">Added</dt>
                  <dd className="text-neutral-700">
                    {formatDateInTz(area.created_at, timezone)}
                  </dd>
                </div>
                {!area.irrigation_zone && (
                  <p className="text-sm text-neutral-400">No management details recorded</p>
                )}
              </dl>
            </CardContent>
          </Card>
        </div>

        {/* Notes */}
        {area.notes && (
          <Card>
            <CardContent className="p-5">
              <h3 className="text-sm font-semibold text-neutral-700 mb-2">Notes</h3>
              <p className="text-sm text-neutral-600 whitespace-pre-wrap">{area.notes}</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

export default CultivarAreaDetail;
